import { type ReactNode, useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { endOfDay, startOfDay } from 'date-fns'

import { MegaphoneIcon } from '@gto/icons-react/outline/megaphone'
import { Alert } from '@gto/ui/alert'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { DateRangePicker } from '@gto/ui/date-picker'
import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'
import { Typography } from '@gto/ui/typography'

import { useCampaignStats } from '@/api/campaign-stats-hooks'
import { useListCouponTags } from '@/api/coupon-hooks'
import { BarCell } from '@/components/bar-cell'
import { DataTable } from '@/components/data-table'
import { EmptyState } from '@/components/empty-state'
import { SelectField } from '@/components/form'
import { InlineQueryError } from '@/components/inline-query-error'
import { StatStrip } from '@/components/stat-strip'
import { StatsCard } from '@/components/stats-card'
import { fluidColumn } from '@/components/table-columns'
import type {
  AffiliateCampaignRowSchema,
  AffiliateCampaignSeriesRowSchema,
} from '@/domain/campaigns'
import { campaignRowId, funnelRatePct } from '@/domain/campaigns'
import { useDateShortcuts } from '@/hooks/use-date-shortcuts'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { toDateStr } from '@/utils/date-str'
import type { ReportGranularity } from '@/utils/report-period'
import { isReportGranularity } from '@/utils/report-period'
import { toAmount } from '@/utils/to-amount'

import { CampaignDetail } from './campaign-detail'
import { CouponTagPill } from './coupon-tag-pill'

const ALL_CAMPAIGNS = ''

type CampaignGridRow = AffiliateCampaignRowSchema & { id: string }

const useCampaignColumns = (maxRevenue: number): ColumnDef<CampaignGridRow>[] => {
  const { formatMessage } = useIntl()

  const money = (
    field: 'revenue_usd' | 'commission_usd' | 'net_usd',
    headerId: string,
    color?: 'primary',
  ): ColumnDef<CampaignGridRow> => ({
    accessorKey: field,
    header: formatMessage({ id: headerId }),
    ...fluidColumn({ min: 110, flex: 0.8, align: 'right' }),
    enableSorting: false,
    cell: ({ row }) => (
      <Typography
        color={color}
        variant="body2"
      >
        <FormattedNumber
          currency="USD"
          maximumFractionDigits={0}
          style="currency"
          value={toAmount(row.original[field], `campaigns table ${field}:`)}
        />
      </Typography>
    ),
  })

  const count = (
    field: 'codes_minted' | 'codes_attempted' | 'codes_converted' | 'orders',
    headerId: string,
  ): ColumnDef<CampaignGridRow> => ({
    accessorKey: field,
    header: formatMessage({ id: headerId }),
    ...fluidColumn({ min: 90, flex: 0.5, align: 'right' }),
    enableSorting: false,
    cell: ({ getValue }) => <FormattedNumber value={Number(getValue() ?? 0)} />,
  })

  return createColumns<CampaignGridRow>([
    {
      accessorKey: 'tag',
      header: formatMessage({ id: 'admin.partnerDetail.campaigns.col.campaign' }),
      ...fluidColumn({ min: 160, flex: 1 }),
      enableSorting: false,
      cell: ({ row }) =>
        row.original.tag ? (
          <CouponTagPill tag={row.original.tag} />
        ) : (
          <Typography
            color="text.secondary"
            variant="body2"
          >
            <FormattedMessage id="admin.partnerDetail.campaigns.untagged" />
          </Typography>
        ),
    },
    count('codes_minted', 'admin.partnerDetail.campaigns.col.codes'),
    count('codes_attempted', 'admin.partnerDetail.campaigns.col.attempted'),
    count('codes_converted', 'admin.partnerDetail.campaigns.col.converted'),
    {
      id: 'conversion_rate',
      header: formatMessage({ id: 'admin.partnerDetail.campaigns.col.conversion' }),
      ...fluidColumn({ min: 100, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => {
        const rate = funnelRatePct(row.original.codes_converted, row.original.codes_attempted)

        // A percentage's scale is 100, not the largest row — bars here compare
        // each campaign against a perfect funnel, which is the honest baseline.
        // Rates can exceed 100 (one code can convert twice); BarCell clamps.
        return (
          <BarCell
            barClassName="bg-primary-main"
            sharePct={rate ?? 0}
            value={
              rate === null ? (
                '—'
              ) : (
                <>
                  <FormattedNumber
                    maximumFractionDigits={0}
                    value={rate}
                  />
                  %
                </>
              )
            }
          />
        )
      },
    },
    count('orders', 'admin.partnerDetail.campaigns.col.orders'),
    {
      accessorKey: 'revenue_usd',
      header: formatMessage({ id: 'admin.partnerDetail.campaigns.col.revenue' }),
      ...fluidColumn({ min: 120, flex: 0.9, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => {
        const value = toAmount(row.original.revenue_usd, 'campaigns table revenue_usd:')

        return (
          <BarCell
            // Scaled to the biggest campaign in the loaded set, so the column
            // ranks the partner's campaigns against each other at a glance.
            sharePct={maxRevenue > 0 ? (value / maxRevenue) * 100 : 0}
            value={
              <FormattedNumber
                currency="USD"
                maximumFractionDigits={0}
                style="currency"
                value={value}
              />
            }
          />
        )
      },
    },
    money('commission_usd', 'admin.partnerDetail.campaigns.col.commission', 'primary'),
    money('net_usd', 'admin.partnerDetail.campaigns.col.net'),
  ])
}

type CampaignPanelProps = {
  isLoading: boolean
  row: AffiliateCampaignRowSchema | undefined
  series: AffiliateCampaignSeriesRowSchema[] | null
  granularityToggle: ReactNode
  partnerTierMissing?: boolean
}

/** Keeps the single-campaign slot from rendering blank while loading, or when a
 *  validly-selected tag comes back with no row at all. */
const CampaignPanel = ({
  isLoading,
  row,
  series,
  granularityToggle,
  partnerTierMissing,
}: CampaignPanelProps) => {
  if (isLoading) {
    return (
      <EmptyState title={<FormattedMessage id="admin.partnerDetail.campaigns.detail.loading" />} />
    )
  }

  if (!row) {
    return (
      <EmptyState title={<FormattedMessage id="admin.partnerDetail.campaigns.detail.empty" />} />
    )
  }

  return (
    <CampaignDetail
      granularityToggle={granularityToggle}
      partnerTierMissing={partnerTierMissing}
      row={row}
      series={series}
    />
  )
}

export const CampaignsTab = ({ partnerId }: { partnerId: string }) => {
  const { formatMessage } = useIntl()
  const shortcuts = useDateShortcuts()

  // All time is the default: "how did WSOP do" is the question this tab answers,
  // which differs from the program Reports page's trailing month.
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null])
  const [granularity, setGranularity] = useState<ReportGranularity>('monthly')
  const [selectedTagId, setSelectedTagId] = useState<string>(ALL_CAMPAIGNS)

  const [from, to] = dateRange
  const statsQuery = useCampaignStats(partnerId, {
    ...(from ? { date_from: toDateStr(from) } : {}),
    ...(to ? { date_to: toDateStr(to) } : {}),
    // Granularity travels with tag_id because it only buckets `series`, which
    // the comparison response omits — sending it there would key the cache on a
    // value that cannot change a single rendered figure.
    ...(selectedTagId ? { granularity, tag_id: selectedTagId } : {}),
  })
  const { data: stats, isLoading, isError, refetch } = statsQuery

  // The selector reads the partner's whole tag catalogue, not the loaded rows,
  // so it stays complete while the stats query is loading or failed.
  const tagsQuery = useListCouponTags(partnerId)
  const { data: tags } = tagsQuery

  useReportQueryError(statsQuery, 'useCampaignStats failed: ')
  useReportQueryError(tagsQuery, 'useListCouponTags (campaigns selector) failed: ')

  const rows = (stats?.rows ?? []).map((row) => ({ ...row, id: campaignRowId(row) }))
  const maxRevenue = Math.max(
    0,
    ...rows.map((row) => toAmount(row.revenue_usd, 'campaigns table max revenue_usd:')),
  )
  const columns = useCampaignColumns(maxRevenue)

  // Rendered only inside the detail view's chart card, never in the shared
  // control row: the comparison table has no series for it to bucket, so a
  // toggle there would be a control that changes nothing on screen.
  const granularityToggle = (
    <ToggleButtonGroup
      size="small"
      value={granularity}
      onValueChange={(value) => {
        if (isReportGranularity(value)) {
          setGranularity(value)
        }
      }}
    >
      <ToggleButton value="daily">
        {formatMessage({ id: 'dashboard.granularity.daily' })}
      </ToggleButton>
      <ToggleButton value="weekly">
        {formatMessage({ id: 'dashboard.granularity.weekly' })}
      </ToggleButton>
      <ToggleButton value="monthly">
        {formatMessage({ id: 'dashboard.granularity.monthly' })}
      </ToggleButton>
      <ToggleButton value="yearly">
        {formatMessage({ id: 'dashboard.granularity.yearly' })}
      </ToggleButton>
    </ToggleButtonGroup>
  )

  const controls = (
    <div className="flex flex-wrap items-end gap-2">
      <SelectField
        className="min-w-52"
        label={formatMessage({ id: 'admin.partnerDetail.campaigns.selector' })}
        value={selectedTagId}
        options={[
          {
            value: ALL_CAMPAIGNS,
            label: formatMessage({ id: 'admin.partnerDetail.campaigns.all' }),
          },
          ...(tags ?? []).map((tag) => ({ value: tag.id, label: tag.name })),
        ]}
        onValueChange={(value) => setSelectedTagId(value)}
      />
      <DateRangePicker
        closeOnSelect={false}
        presets={shortcuts}
        value={dateRange}
        onChange={([newFrom, newTo], context) => {
          if (context.validationError.some(Boolean)) {
            return
          }
          setDateRange([newFrom ? startOfDay(newFrom) : null, newTo ? endOfDay(newTo) : null])
        }}
      />
    </div>
  )

  if (isError) {
    return (
      <div
        className="flex flex-col gap-3"
        data-tst="campaigns-tab"
      >
        {controls}
        <InlineQueryError
          data-tst="campaigns-error"
          messageId="admin.partnerDetail.campaigns.error"
          onRetry={() => void refetch()}
        />
      </div>
    )
  }

  return (
    <div
      className="flex flex-col gap-3"
      data-tst="campaigns-tab"
    >
      {controls}

      {selectedTagId ? (
        <CampaignPanel
          granularityToggle={granularityToggle}
          isLoading={isLoading}
          partnerTierMissing={stats?.partner_tier_missing}
          row={stats?.rows[0]}
          series={stats?.series ?? null}
        />
      ) : (
        <StatsCard
          icon={<MegaphoneIcon />}
          title={<FormattedMessage id="admin.partnerDetail.campaigns.title" />}
        >
          {stats && stats.overlapping_code_count > 0 ? (
            <Alert
              className="mb-3"
              data-tst="campaigns-overlap-note"
              severity="info"
            >
              <FormattedMessage
                id="admin.partnerDetail.campaigns.overlapNote"
                values={{ count: stats.overlapping_code_count }}
              />
            </Alert>
          ) : null}

          {!isLoading && rows.length === 0 ? (
            <EmptyState title={<FormattedMessage id="admin.partnerDetail.campaigns.empty" />} />
          ) : (
            <>
              <DataTable
                columns={columns}
                data={rows}
                getRowId={(row: AffiliateCampaignRowSchema & { id: string }) => row.id}
                loading={isLoading}
                onRowClick={(row) => {
                  if (row.tag) {
                    setSelectedTagId(row.tag.id)
                  }
                }}
              />

              {stats ? (
                <div className="mt-4 flex flex-col gap-2">
                  <Typography
                    color="text.secondary"
                    variant="caption"
                  >
                    <FormattedMessage id="admin.partnerDetail.campaigns.totals" />
                  </Typography>
                  <StatStrip
                    data-tst="campaigns-totals"
                    cells={[
                      {
                        id: 'codes',
                        label: <FormattedMessage id="admin.partnerDetail.campaigns.col.codes" />,
                        value: <FormattedNumber value={stats.totals.codes_minted} />,
                      },
                      {
                        id: 'orders',
                        label: <FormattedMessage id="admin.partnerDetail.campaigns.col.orders" />,
                        value: <FormattedNumber value={stats.totals.orders} />,
                      },
                      {
                        id: 'revenue',
                        label: <FormattedMessage id="admin.partnerDetail.campaigns.col.revenue" />,
                        value: (
                          <FormattedNumber
                            currency="USD"
                            maximumFractionDigits={0}
                            style="currency"
                            value={toAmount(
                              stats.totals.revenue_usd,
                              'campaigns totals revenue_usd:',
                            )}
                          />
                        ),
                      },
                      {
                        id: 'commission',
                        label: (
                          <FormattedMessage id="admin.partnerDetail.campaigns.col.commission" />
                        ),
                        value: (
                          <FormattedNumber
                            currency="USD"
                            maximumFractionDigits={0}
                            style="currency"
                            value={toAmount(
                              stats.totals.commission_usd,
                              'campaigns totals commission_usd:',
                            )}
                          />
                        ),
                      },
                      {
                        id: 'net',
                        label: <FormattedMessage id="admin.partnerDetail.campaigns.col.net" />,
                        value: (
                          <FormattedNumber
                            currency="USD"
                            maximumFractionDigits={0}
                            style="currency"
                            value={toAmount(stats.totals.net_usd, 'campaigns totals net_usd:')}
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              ) : null}
            </>
          )}
        </StatsCard>
      )}
    </div>
  )
}
